/**
 * ClientDetailContent — pure presentation component for client detail panel
 *
 * Renders: header, contacts, aircraft, contracts, financial snapshot, notes, actions
 */

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Users,
  Plane,
  FileText,
  DollarSign,
  StickyNote,
  Building2,
  UserCircle,
  Landmark,
  Heart,
  Pencil,
  Maximize2,
  PanelRight,
  Mail,
  Phone,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ClientRegistryRow } from '@/types/clients'
import {
  CLIENT_TYPE_LABELS,
  CLIENT_CRM_STAGE_LABELS,
  CLIENT_CRM_STAGE_VARIANTS,
  CLIENT_RELATIONSHIP_LABELS,
  CLIENT_RELATIONSHIP_VARIANTS,
  PAYMENT_TERMS_LABELS,
  getClientDisplayName,
} from '@/types/clients'
import type { ClientType, PaymentTerms } from '@/types/clients'
import type { ClientDetailContact, ClientDetailLease, ClientDetailTransient, ClientDetailAircraft } from '@/hooks/useClientDetail'
import type { DetailPanelMode } from '@/stores/clientsRegistryStore'

// ============================================
// Props
// ============================================

interface ClientDetailContentProps {
  client: ClientRegistryRow
  contacts: ClientDetailContact[]
  leases: ClientDetailLease[]
  transients: ClientDetailTransient[]
  aircraft: ClientDetailAircraft[]
  panelMode: DetailPanelMode
  onToggleMode: () => void
  onEdit: (id: string) => void
  onManageContacts: (id: string) => void
  onLeaseClick?: (leaseId: string) => void
  onTransientClick?: (stayId: string) => void
  onAircraftClick?: (aircraftId: string) => void
  onViewContracts?: () => void
  onViewAircraft?: () => void
}

// ============================================
// Icon map
// ============================================

const CLIENT_TYPE_ICONS: Record<ClientType, React.ComponentType<{ className?: string }>> = {
  corporate: Building2,
  individual: UserCircle,
  government: Landmark,
  non_profit: Heart,
}

// ============================================
// Section wrapper
// ============================================

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span>{title}</span>
      </div>
      <div className="pl-6 space-y-1.5">{children}</div>
    </div>
  )
}

// ============================================
// Contact role flags
// ============================================

function ContactRoleFlags({ contact }: { contact: ClientDetailContact }) {
  const flags: string[] = []
  if (contact.is_primary_contact) flags.push('Primary')
  if (contact.is_billing_contact) flags.push('Billing')
  if (contact.is_operations_contact) flags.push('Ops')
  if (flags.length === 0) return null
  return (
    <span className="text-[10px] text-muted-foreground">
      ({flags.join(', ')})
    </span>
  )
}

// ============================================
// Main Component
// ============================================

export function ClientDetailContent({
  client,
  contacts,
  leases,
  transients,
  aircraft,
  panelMode,
  onToggleMode,
  onEdit,
  onManageContacts,
  onLeaseClick,
  onTransientClick,
  onAircraftClick,
  onViewContracts,
  onViewAircraft,
}: ClientDetailContentProps) {
  const TypeIcon = CLIENT_TYPE_ICONS[client.client_type]
  const displayName = getClientDisplayName(client)

  return (
    <div className="space-y-5 p-1">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center gap-2">
            <TypeIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <h2 className="text-lg font-semibold truncate">{displayName}</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={CLIENT_CRM_STAGE_VARIANTS[client.crm_stage]} className="text-xs">
              {CLIENT_CRM_STAGE_LABELS[client.crm_stage]}
            </Badge>
            <Badge variant={CLIENT_RELATIONSHIP_VARIANTS[client.computed_relationship]} className="text-xs">
              {CLIENT_RELATIONSHIP_LABELS[client.computed_relationship]}
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">
              {client.client_number}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {CLIENT_TYPE_LABELS[client.client_type]}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onToggleMode}
          title={panelMode === 'sidePanel' ? 'Switch to modal' : 'Switch to sidebar'}
        >
          {panelMode === 'sidePanel' ? (
            <Maximize2 className="h-4 w-4" />
          ) : (
            <PanelRight className="h-4 w-4" />
          )}
        </Button>
      </div>

      <Separator />

      {/* Contacts */}
      <Section icon={Users} title={`Contacts (${contacts.length})`}>
        {contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts</p>
        ) : (
          <>
            {contacts.slice(0, 5).map((c) => (
              <div
                key={c.id}
                className={cn(
                  'text-sm rounded-md p-2 -ml-2',
                  c.is_primary_contact && 'bg-muted/60'
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">
                    {c.first_name} {c.last_name}
                  </span>
                  <ContactRoleFlags contact={c} />
                </div>
                {c.job_title && (
                  <div className="text-xs text-muted-foreground">{c.job_title}</div>
                )}
                <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                  {c.email_primary && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {c.email_primary}
                    </span>
                  )}
                  {(c.phone_mobile || c.phone_office) && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {c.phone_mobile || c.phone_office}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {contacts.length > 5 && (
              <button
                className="text-xs text-primary hover:underline cursor-pointer"
                onClick={() => onManageContacts(client.id)}
              >
                View all {contacts.length} contacts &rarr;
              </button>
            )}
          </>
        )}
      </Section>

      <Separator />

      {/* Aircraft */}
      <Section icon={Plane} title={`Aircraft (${aircraft.length})`}>
        {aircraft.length === 0 ? (
          <p className="text-sm text-muted-foreground">No aircraft registered</p>
        ) : (
          <>
            <div className="space-y-1">
              {aircraft.map((ac) => {
                const modelName = ac.aircraft_type?.name
                  ?? (ac.manufacturer && ac.model ? `${ac.manufacturer} ${ac.model}` : null)
                return (
                  <button
                    key={ac.id}
                    className="w-full text-left text-sm rounded-md p-2 -ml-2 hover:bg-muted/60 cursor-pointer transition-colors flex items-center justify-between gap-2"
                    onClick={() => onAircraftClick?.(ac.id)}
                  >
                    <span className="font-mono font-medium">{ac.tail_number}</span>
                    {modelName && (
                      <span className="text-xs text-muted-foreground truncate">{modelName}</span>
                    )}
                  </button>
                )
              })}
            </div>
            {onViewAircraft && (
              <button
                className="text-xs text-primary hover:underline cursor-pointer mt-1"
                onClick={onViewAircraft}
              >
                View All Aircraft &rarr;
              </button>
            )}
          </>
        )}
      </Section>

      <Separator />

      {/* Active Contracts */}
      <Section icon={FileText} title="Active Contracts">
        {leases.length === 0 && transients.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active contracts</p>
        ) : (
          <>
            {leases.map((lease) => (
              <button
                key={lease.id}
                className="w-full text-left text-sm rounded-md p-2 -ml-2 bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors"
                onClick={() => onLeaseClick?.(lease.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">{lease.contract_number}</span>
                  <Badge variant="outline" className="text-[10px]">{lease.status}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {lease.unit?.unit_code && <span>{lease.unit.unit_code}</span>}
                  {lease.unit?.unit_code && lease.location?.name && <span> &middot; </span>}
                  {lease.location?.name && <span>{lease.location.name}</span>}
                </div>
                {lease.monthly_rate != null && (
                  <div className="text-xs mt-0.5">
                    <span className="font-mono">${Number(lease.monthly_rate).toLocaleString()}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                )}
              </button>
            ))}
            {transients.map((stay) => (
              <button
                key={stay.id}
                className="w-full text-left text-sm rounded-md p-2 -ml-2 bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors"
                onClick={() => onTransientClick?.(stay.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs">{stay.booking_number}</span>
                  <Badge variant="outline" className="text-[10px]">{stay.status}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {stay.location?.name && <span>{stay.location.name}</span>}
                  {stay.check_in && (
                    <span> &middot; {new Date(stay.check_in).toLocaleDateString()}</span>
                  )}
                  {stay.expected_check_out && (
                    <span> &ndash; {new Date(stay.expected_check_out).toLocaleDateString()}</span>
                  )}
                </div>
              </button>
            ))}
            {onViewContracts && (
              <button
                className="text-xs text-primary hover:underline cursor-pointer mt-1"
                onClick={onViewContracts}
              >
                View Contracts &rarr;
              </button>
            )}
          </>
        )}
      </Section>

      <Separator />

      {/* Financial Snapshot */}
      <Section icon={DollarSign} title="Financial Snapshot">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Monthly Committed</span>
            <span className="font-mono">
              {client.active_lease_monthly_total != null && client.active_lease_monthly_total > 0
                ? `$${client.active_lease_monthly_total.toLocaleString()}`
                : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Balance Due</span>
            <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground border-dashed">
              Coming Soon
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">LTV</span>
            <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground border-dashed">
              Coming Soon
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Payment Terms</span>
            <span>
              {client.payment_terms && PAYMENT_TERMS_LABELS[client.payment_terms as PaymentTerms]
                ? PAYMENT_TERMS_LABELS[client.payment_terms as PaymentTerms]
                : '—'}
            </span>
          </div>
        </div>
      </Section>

      {/* Notes */}
      {client.notes && (
        <>
          <Separator />
          <Section icon={StickyNote} title="Notes">
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {client.notes}
            </p>
          </Section>
        </>
      )}

      <Separator />

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(client.id)}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={() => onManageContacts(client.id)}>
          <Users className="mr-1.5 h-3.5 w-3.5" />
          Manage Contacts
        </Button>
      </div>
    </div>
  )
}
